import React, { useMemo } from 'react';
import {
  CheckCircleIcon,
  FormItem,
  Radio,
  RadioGroup,
  Text,
} from 'elmo-elements';
import { ElearningPublishChannel } from '@/modules/types/elearning/publish.enum';
import { useFlags } from 'launchdarkly-react-client-sdk';
import './Header.scss';
import { DATETIME_FORMAT } from '@/app/const';
import { format as formatDate } from 'date-fns';
import { useParams } from 'react-router-dom';
import { ElearningParamType } from '../../routes';
import {
  useGetCurrentTenantProfileQuery,
  useGetElearningDetailsQuery,
} from '@/modules/services';
import { ChannelsPublishDateType } from '@/modules/types/elearning';

const channelList = [
  {
    value: ElearningPublishChannel.Generic,
    label: 'Learning Management System',
  },
  { value: ElearningPublishChannel.SCORM, label: 'SCORM Package' },
  { value: ElearningPublishChannel.CourseLibrary, label: 'Course Library' },
  { value: ElearningPublishChannel.VideoLibrary, label: 'Video Library' },
  { value: ElearningPublishChannel.BREATHE, label: 'Breathe' },
];

interface ChannelSelectListProps {
  onChange?: (value: number) => void;
}

const showPublishedInfo = (
  channel: ElearningPublishChannel,
  channelPublishDate?: ChannelsPublishDateType | null,
) => {
  const CHANNEL_TO_PROPERTY_MAP: Record<
    ElearningPublishChannel,
    keyof ChannelsPublishDateType
  > = {
    [ElearningPublishChannel.Generic]: 'lms',
    [ElearningPublishChannel.CourseLibrary]: 'courseLibrary',
    [ElearningPublishChannel.VideoLibrary]: 'videoLibrary',
    [ElearningPublishChannel.SCORM]: 'scorm',
    [ElearningPublishChannel.BREATHE]: 'breathe',
  };

  const propertyName = CHANNEL_TO_PROPERTY_MAP[channel];
  const date = channelPublishDate?.[propertyName];

  return (
    <div>
      <Text color={`${date ? 'success' : 'gray'}`} size="sm">
        <CheckCircleIcon size="sm" />
      </Text>

      <span className="publish-info">
        {date
          ? `Last published ${formatDate(new Date(date), DATETIME_FORMAT)}`
          : 'Not published'}
      </span>
    </div>
  );
};

const ChannelSelectList = ({ onChange }: ChannelSelectListProps) => {
  const { elearningId } = useParams<ElearningParamType>();
  const { data } = useGetElearningDetailsQuery(elearningId);
  const { data: tenantProfileData } = useGetCurrentTenantProfileQuery();
  const {
    learningElaScormExportEnable: isScormExportEnabled,
    learningElaCourseLibraryAdmin: isElaCourseLibraryEnabled,
    learningElaBreatheIntegration: isBreatheFlag,
  } = useFlags();

  // Check if SCORM is enabled
  const isScormEnabled = isScormExportEnabled && tenantProfileData?.scorm;
  const isBreatheEnabled =
    isBreatheFlag && tenantProfileData?.configMetadata?.enableBreathe;

  // Filter channel list based on feature flags
  const filteredChannelList = useMemo(
    () =>
      channelList.filter((channel) => {
        if (channel.value === ElearningPublishChannel.SCORM) {
          return isScormEnabled;
        }
        if (
          channel.value === ElearningPublishChannel.CourseLibrary ||
          channel.value === ElearningPublishChannel.VideoLibrary
        ) {
          return isElaCourseLibraryEnabled;
        }
        if (channel.value === ElearningPublishChannel.BREATHE) {
          return isBreatheEnabled;
        }
        return true;
      }),
    [isScormEnabled, isElaCourseLibraryEnabled, isBreatheEnabled],
  );

  return (
    <FormItem label="Available channels:">
      <RadioGroup
        className="channel-container"
        onChange={(value) => {
          if (onChange) onChange(Number(value));
        }}
        direction="vertical"
      >
        {filteredChannelList.map((channel) => (
          <Radio
            className="channel-item"
            key={channel.value}
            value={channel.value}
          >
            {channel.label}
            {showPublishedInfo(channel.value, data?.date.channels)}
          </Radio>
        ))}
      </RadioGroup>
    </FormItem>
  );
};

export default ChannelSelectList;
